import { isEmptyString } from '@/utils/string';
import { Button, Modal, ModalBody, ModalFooter } from '@customerio/pluma-components/react';
import { createContext, useContext, useState } from 'react';

export type ConfirmOptions = {
  /**
   * Title for the current confirmation
   */
  title: string;
  /**
   * Message for the current confirmation
   */
  message?: string;

  /**
   * Callback function to execute when the user confirms the action
   */
  onConfirm?: () => void | Promise<void>;

  /**
   * Callback function to execute when the user cancels the action
   */
  onCancel?: () => void | Promise<void>;

  /**
   * Whether the confirmation dialog is destructive
   */
  isDanger?: boolean;

  /**
   * Custom text for the confirm button
   */
  confirmText?: string;

  /**
   * Custom text for the cancel button
   */
  cancelText?: string;
};

type ConfirmState =
  | (ConfirmOptions & {
      /**
       * Whether the confirmation dialog is currently open
       */
      isOpen: true;

      /**
       * Function to resolve the confirmation dialog
       */
      resolver: (result: boolean) => void;
    })
  | (Partial<ConfirmOptions> & {
      /**
       * Whether the confirmation dialog is currently open
       */
      isOpen: false;
    });

const ConfirmContext = createContext<{
  state: ConfirmState;
  updateState: (newState: ConfirmState) => void;
} | null>(null);

export function useConfirm() {
  const confirmContext = useContext(ConfirmContext);

  if (confirmContext == null) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }

  const confirm = async (options: ConfirmOptions): Promise<boolean> => {
    // if we are already open, fail the confirmation
    if (confirmContext.state.isOpen) {
      try {
        await options.onCancel?.();
      } catch {
        // ignore any errors from the onCancel callback
      }
      return false;
    }

    return new Promise<boolean>((resolve) => {
      confirmContext.updateState({
        isOpen: true,
        resolver: resolve,
        ...options,
      });
    });
  };

  return confirm;
}

export function ConfirmProvider({ children }: { children?: React.ReactNode }) {
  const [state, setState] = useState<ConfirmState>({
    isOpen: false,
  });

  const handleConfirm = async () => {
    if (!state.isOpen) {
      return;
    }

    try {
      await state.onConfirm?.();
    } catch {
      // ignore any errors from the onConfirm callback
    }
    state.resolver(true);
    setState((s) => ({ ...s, isOpen: false }));
  };

  const handleCancel = async () => {
    if (!state.isOpen) {
      return;
    }

    try {
      await state.onCancel?.();
    } catch {
      // ignore any errors from the onCancel callback
    }
    state.resolver(false);
    setState((s) => ({ ...s, isOpen: false }));
  };

  // we clear out the state when the modal is closed
  // so that there isn't a flash when the model is closing
  // as the settings shift into the default options
  function clearState() {
    setState({
      isOpen: false,
    });
  }

  return (
    <ConfirmContext.Provider value={{ state, updateState: setState }}>
      <Modal
        isOpen={state.isOpen}
        shouldShowCloseButton={false}
        shouldCloseOnEscapePress={true}
        onClose={handleCancel}
        onCloseComplete={clearState}
        title={state.title}
        size="sm"
      >
        {!isEmptyString(state.message) ? <ModalBody>{state.message}</ModalBody> : null}
        <ModalFooter>
          <Button onClick={handleCancel}>{state.cancelText ?? 'Cancel'}</Button>
          <Button variant="primary" onClick={handleConfirm} isDanger={state.isDanger}>
            {state.confirmText ?? 'Confirm'}
          </Button>
        </ModalFooter>
      </Modal>
      {children}
    </ConfirmContext.Provider>
  );
}
